import { Product } from '../types';
import { QuantityControl } from './QuantityControl';
import { useCartStore } from '../stores/useCartStore';
import { useAuthStore } from '../stores/useAuthStore';
import { useCurrencySymbol } from '../lib/currency';
import { Plus, Star, Clock } from 'lucide-react';
import { formatPreparationTime } from '../lib/format';
import { getFullImageUrl } from '../lib/api';

interface ProductCardProps {
  product: Product;
  scheduledDate?: string; // ISO date string for scheduled orders
  disabled?: boolean;
  disabledMessage?: string;
  onOpenDetail?: (product: Product) => void;
}

export function ProductCard({ product, scheduledDate, disabled, disabledMessage, onOpenDetail }: ProductCardProps) {
  const {
    addItem,
    items,
    updateQuantity,
    addScheduledItem,
    scheduledOrders,
    updateScheduledQuantity
  } = useCartStore();
  const { isOpen } = useAuthStore();
  const currencySymbol = useCurrencySymbol();

  const hasSubProducts = product.sub_products && product.sub_products.length > 0;

  // For products with sub-products, we always want to trigger the modal
  // For simple products, we show the quantity control
  const totalQuantity = scheduledDate
    ? scheduledOrders
      .find(order => order.scheduledDate === scheduledDate)
      ?.items.filter(item => item.product.uuid === product.uuid)
      .reduce((sum, item) => sum + item.quantity, 0) || 0
    : items.filter(item => item.product.uuid === product.uuid)
      .reduce((sum, item) => sum + item.quantity, 0) || 0;

  const handleAddToCart = () => {
    if (disabled) return;
    if (hasSubProducts && onOpenDetail) {
      onOpenDetail(product);
      return;
    }
    if (scheduledDate) {
      addScheduledItem(product, scheduledDate, 1);
    } else {
      addItem(product, 1);
    }
  };

  const handleIncrease = () => {
    if (disabled) return;
    if (hasSubProducts && onOpenDetail) {
      onOpenDetail(product);
      return;
    }
    if (scheduledDate) {
      updateScheduledQuantity(product.uuid, scheduledDate, totalQuantity + 1);
    } else {
      updateQuantity(product.uuid, totalQuantity + 1);
    }
  };

  const handleDecrease = () => {
    if (totalQuantity > 0) {
      if (hasSubProducts && onOpenDetail) {
        onOpenDetail(product); // Or maybe do nothing/redirect to cart
        return;
      }
      if (scheduledDate) {
        updateScheduledQuantity(product.uuid, scheduledDate, totalQuantity - 1);
      } else {
        updateQuantity(product.uuid, totalQuantity - 1);
      }
    }
  };

  return (
    <div 
      className={`bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-[1.02] ${disabled ? 'opacity-75 grayscale-[0.5]' : ''}`}
    >
      <div 
        className="cursor-pointer"
        onClick={handleAddToCart}
      >
        <div className="aspect-[4/3] overflow-hidden relative">
          <img
            src={getFullImageUrl(product.image)}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
          />
          <div className="absolute top-3 right-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
            < Star className="w-3 h-3 text-yellow-500 fill-current" />
            <span className="text-xs font-bold text-gray-900 dark:text-white">4.8</span>
          </div>
        </div>

        <div className="p-5 pb-0">
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-bold text-lg text-gray-900 dark:text-white leading-tight">
              {product.name}
            </h3>
            <span className="text-lg font-bold text-gray-900 dark:text-white">
              {currencySymbol}{product.price.toFixed(2)}
            </span>
          </div>

          {product.preparation_time && product.preparation_time !== "00:00:00" && (
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              <Clock className="w-3.5 h-3.5" />
              <span>{formatPreparationTime(product.preparation_time)}</span>
            </div>
          )}

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-5 whitespace-pre-line">
            {product.description}
          </p>
        </div>
      </div>

      <div className="p-5 pt-0">
        <div className="flex items-center justify-between">
          {!hasSubProducts && totalQuantity > 0 ? (
            <QuantityControl
              quantity={totalQuantity}
              onIncrease={handleIncrease}
              onDecrease={handleDecrease}
              max={product.stock ?? Infinity}
            />
          ) : (
            <button
              onClick={handleAddToCart}
              disabled={(product.stock !== null && product.stock <= 0) || (!isOpen && !scheduledDate) || disabled}
              className={`btn-primary px-5 py-3 rounded-xl text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed w-full ${((!isOpen && !scheduledDate) || disabled) ? 'grayscale bg-gray-400 border-gray-400' : ''}`}
            >
              <div className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                {(!isOpen && !scheduledDate) ? 'Cerrado' : (disabled ? (disabledMessage || 'No disponible') : ((product.stock !== null && product.stock <= 0) ? 'Sin Stock' : 'Agregar'))}
              </div>
              {hasSubProducts && totalQuantity > 0 && (
                <span className="ml-auto bg-white/20 px-2 py-0.5 rounded-full text-[10px]">
                  {totalQuantity}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
    </div >
  );
}